import { Fragment, useEffect, useState } from 'react'
import { api, type Desk, type Rule, type Scope } from '../api'
import { Cited } from '../components/Cited'
import { RetireLimit } from '../components/RetireLimit'
import { RowMenu } from '../components/RowMenu'
import { layerLabel, says } from '../limits'

/**
 * Fund groups — TECHNICAL_DESIGN.md §5.6, §10.1.
 *
 * `POST /api/scopes` has existed since the store was built and nothing in the
 * application called it, so a group could be created only by somebody able to
 * construct a request. The same gap portfolios had, found the same way: by
 * somebody looking for the menu item.
 *
 * **A group binds nothing when it is created.** It is an empty set with a name
 * until an approver attaches a limit to it and an administrator puts funds in
 * it — two different people, which is §10.1 rather than an inconvenience: one
 * decides what a group means, the other which funds live with that meaning.
 *
 * **A group belongs to one desk or to the whole firm** (decision 83). A desk's group
 * holds only that desk's funds and its limits are the desk's; the firm's groups are
 * compliance's and may hold funds from any desk.
 *
 * The `type` is presentational. Resolution never walks a hierarchy — a fund is
 * simultaneously India-domiciled, emerging-market debt and UCITS, and no tree
 * holds that — so this is a label for people reading the list, not a structure
 * the store reasons about.
 */
const TYPES = [
  { id: 'firm', blurb: 'A policy the whole firm runs under' },
  { id: 'market', blurb: 'An asset class or strategy — EM debt, UCITS equity' },
  { id: 'region', blurb: 'A geography the funds are managed for' },
  { id: 'country', blurb: 'A single country' },
  { id: 'jurisdiction', blurb: 'A regulatory regime the funds fall under' },
]

export function Groups({
  canCreate = true,
  canDetach = false,
  firmWide = true,
  desk: openOn,
  onBack,
}: {
  /** Works across the whole firm: sees whose each group is, and may make one of the firm's. */
  firmWide?: boolean
  /** The desk to show, arriving from Desks: its groups and the firm's groups over its funds. */
  desk?: string
  /** Back to Desks, where the reader came from. */
  onBack?: () => void
  /** Adding and deleting groups is the administrator's (scope.create). */
  canCreate?: boolean
  /** Proposing to detach a limit, which an approver who did not propose it decides
   *  (decision 72). Detaching ends the limit for every fund in the group. */
  canDetach?: boolean
} = {}) {
  const [scopes, setScopes] = useState<Scope[]>([])
  /** What each group carries, keyed by group. Fetched per group rather than in
   *  one call because the store answers per scope — and a group with nothing on
   *  it is the case worth showing plainly, not the case worth optimising. */
  const [rules, setRules] = useState<Record<string, Rule[]>>({})
  const [open, setOpen] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [form, setForm] = useState({ type: 'market', code: '', name: '', desk_id: '' })
  const [desks, setDesks] = useState<Desk[]>([])
  /** Which desk's groups the list shows; empty is every group the reader can see. */
  const [shown, setShown] = useState(openOn ?? '')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [detaching, setDetaching] = useState<string | null>(null)

  /** Delete a group that never held anything. Confirmed rather than silent
   *  because the name disappears from every dropdown, and refused by the store
   *  the moment the group has a past — a fund that belonged to it genuinely did,
   *  and no tidying-up may make the store deny that. */
  async function remove(scope: Scope) {
    if (
      !window.confirm(
        `Delete ${scope.code}? It holds no funds and carries no limits, so ` +
          'nothing is bound by it and nothing changes for anyone. If it ever ' +
          'held either, this will be refused.',
      )
    )
      return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await api.deleteScope(scope.id)
      setNotice(`${scope.code} deleted.`)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function load() {
    setError(null)
    try {
      const found = await api.scopes()
      setScopes(found)
      // What each carries, alongside. A group's limits are the reason it exists,
      // so they arrive with the list rather than behind a second click.
      const carried = await Promise.all(
        found.map(async (s) => [s.id, await api.scopeRules(s.id)] as const),
      )
      setRules(Object.fromEntries(carried))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => {
    void load()
    api
      .desks()
      .then((found) => {
        setDesks(found)
        // Somebody on one desk makes that desk's groups; nobody has to say so.
        if (!firmWide && found.length === 1) setForm((f) => ({ ...f, desk_id: found[0]!.id }))
      })
      .catch(() => setDesks([]))
  }, [])

  /** Whose groups these are is worth a column once there is more than one owner to tell apart. */
  const showOwner = firmWide || desks.length > 1
  /** A desk's groups, and the firm's groups over its funds, which bind them too. */
  const listed = shown
    ? scopes.filter((s) => s.desk_id === shown || (!s.desk_id && (s.member_desk_ids ?? []).includes(shown)))
    : scopes
  const fixedDesk = openOn ? desks.find((d) => d.id === openOn) : undefined

  if (!loaded) return <p className="loading">Loading…</p>

  return (
    <section>
      <header className="page-head">
        <h2>Fund groups</h2>
        <p className="note">
          A group is a set of funds that share limits — an asset class, a
          regulatory regime, a firm-wide policy. A limit attached to the group
          applies to every fund in it, which is how one policy covers thirty
          mandates without being typed thirty times.
        </p>
      </header>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {canCreate && (
      <div className="card">
        <h3>Add a group</h3>
        <form
          className="row-form"
          onSubmit={(e) => {
            e.preventDefault()
            setBusy(true)
            setError(null)
            setNotice(null)
            api
              .createScope({ ...form, desk_id: form.desk_id || null })
              .then((made) => {
                setNotice(
                  `${form.code} added${made.desk_code ? ` to ${made.desk_code}` : ' for the whole firm'}. ` +
                    'It carries no limits until an approver attaches one.',
                )
                setForm({ type: 'market', code: '', name: '', desk_id: firmWide ? '' : form.desk_id })
                return load()
              })
              .catch((e) => setError(e instanceof Error ? e.message : String(e)))
              .finally(() => setBusy(false))
          }}
        >
          <label>
            Kind
            <select
              value={form.type}
              title="A label for people reading the list. It does not change how limits are worked out."
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id}
                </option>
              ))}
            </select>
          </label>
          <label>
            Code
            <input
              value={form.code}
              required
              title="Short, and how the group is referred to elsewhere — EM_DEBT, UCITS."
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </label>
          <label>
            Name
            <input
              value={form.name}
              required
              title="What somebody reading a limit should see this group called."
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          {(firmWide || desks.length > 1) && (
            <label>
              Belongs to
              <select
                value={form.desk_id}
                required={!firmWide}
                title="A desk's group holds only that desk's funds, and the desk sets its limits. The whole firm's groups are compliance's and may hold any desk's funds."
                onChange={(e) => setForm({ ...form, desk_id: e.target.value })}
              >
                {firmWide ? <option value="">Whole firm</option> : <option value="">Choose a desk…</option>}
                {desks.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.code} · {d.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="submit" disabled={busy}>
            Add
          </button>
        </form>
        {!firmWide && desks.length === 1 && (
          <p className="note">Groups you add belong to {desks[0]!.code} and hold only its funds.</p>
        )}
        <p className="note">{TYPES.find((t) => t.id === form.type)?.blurb}</p>
        <p className="note">
          Creating a group applies nothing. It is an empty set until an approver
          attaches a limit to it, and until funds are put in it — which is done on{' '}
          <strong>View funds in a group</strong>.
        </p>
      </div>
      )}

      {openOn && (
        <p className="back" data-testid="desk-view">
          <button type="button" className="link-button" onClick={onBack}>
            ← Desks
          </button>{' '}
          Groups of <strong>{fixedDesk ? `${fixedDesk.code} · ${fixedDesk.name}` : 'this desk'}</strong>
        </p>
      )}
      {!openOn && showOwner && desks.length > 0 && (
        <label>
          Desk
          <select aria-label="Show groups of" value={shown} onChange={(e) => setShown(e.target.value)}>
            <option value="">Every group</option>
            {desks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} · {d.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <h3>{listed.length} group(s)</h3>
      {listed.length === 0 ? (
        <p className="empty">None yet.</p>
      ) : (
        <table className="grid limits has-menus">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Kind</th>
              {showOwner && <th>Belongs to</th>}
              <th>Funds in it</th>
              <th>Limits it carries</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {listed.map((scope) => {
              const carried = rules[scope.id]
              return (
                <Fragment key={scope.id}>
                  <tr data-testid="group">
                    <td>
                      <code>{scope.code}</code>
                    </td>
                    <td>{scope.name !== scope.code ? scope.name : ''}</td>
                    <td className="clause">{scope.type}</td>
                    {showOwner && (
                      <td data-testid="group-owner" title={scope.desk_name ?? 'Compliance’s, across every desk'}>
                        {scope.desk_code ?? <span className="clause">Whole firm</span>}
                      </td>
                    )}
                    <td className="limit">{scope.portfolio_count}</td>
                    <td>
                      {/* The whole point of a group is the limits it carries,
                          and this table listed a code, a name and a fund count.
                          A promoted limit landed correctly and appeared
                          nowhere. */}
                      {carried === undefined ? (
                        <span className="clause">counting…</span>
                      ) : carried.length === 0 ? (
                        <span className="clause">none yet</span>
                      ) : (
                        <button
                          className="ghost"
                          aria-expanded={open === scope.id}
                          onClick={() => setOpen(open === scope.id ? null : scope.id)}
                        >
                          {carried.length} limit(s)
                        </button>
                      )}
                    </td>
                    <td className="row-actions">
                      {/* Offered only while the group is empty. The store still
                          refuses one that was *ever* occupied — emptying a group
                          must not become a way to erase it — and says so in
                          words if this button is pressed on one. */}
                      {canCreate && scope.portfolio_count === 0 && carried?.length === 0 && (
                        <RowMenu
                          label={`Actions for ${scope.code}`}
                          disabled={busy}
                          items={[{ label: 'Delete', danger: true, onSelect: () => void remove(scope) }]}
                        />
                      )}
                    </td>
                  </tr>
                  {open === scope.id && carried && carried.length > 0 && (
                    <tr className="history-row">
                      <td colSpan={showOwner ? 7 : 6}>
                        <p className="note">
                          Every fund in <code>{scope.code}</code> is bound by
                          these, wherever they were written.
                        </p>
                        <table className="grid limits">
                          <thead>
                            <tr>
                              <th>Limit</th>
                              <th>Says</th>
                              <th>Set by</th>
                              <th>Cites</th>
                              {canDetach && <th />}
                            </tr>
                          </thead>
                          <tbody>
                            {carried.map((rule) => {
                              const said = says(rule)
                              return (
                                <Fragment key={rule.id}>
                                <tr data-testid="group-rule">
                                  <td>
                                    <code>{rule.rule_key}</code>
                                  </td>
                                  <td>
                                    <span
                                      className={said.numeric ? 'limit' : 'no-number'}
                                    >
                                      {said.text}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="pill pill-layer">
                                      {layerLabel(rule.layer)}
                                    </span>
                                  </td>
                                  <td>
                                    <Cited rule={rule} />
                                  </td>
                                  {canDetach && (
                                    <td className="row-actions">
                                      <RowMenu
                                        label={`Actions for ${rule.rule_key}`}
                                        items={[
                                          {
                                            label: 'Propose to detach',
                                            danger: true,
                                            onSelect: () => {
                                              setNotice(null)
                                              setDetaching(rule.id)
                                            },
                                          },
                                        ]}
                                      />
                                    </td>
                                  )}
                                </tr>
                                {detaching === rule.id && (
                                  <tr className="history-row">
                                    <td colSpan={5}>
                                      <p className="note">
                                        Detaching ends it for all {scope.portfolio_count} fund(s) in{' '}
                                        <code>{scope.code}</code>. Nothing is deleted: it stays the
                                        right answer for the period it applied.
                                      </p>
                                      <RetireLimit
                                        ruleId={rule.id}
                                        what={`${rule.rule_key} on ${scope.code}`}
                                        onDone={(message) => {
                                          setDetaching(null)
                                          setNotice(message)
                                        }}
                                        onCancel={() => setDetaching(null)}
                                      />
                                    </td>
                                  </tr>
                                )}
                                </Fragment>
                              )
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}
